import React, { useCallback, useEffect } from 'react';
import { Layout, Spin, ConfigProvider, Button } from 'antd';
import { Unity, useUnityContext } from 'react-unity-webgl';
import Navbar from '../../components/Layout/Navbar';
import './MainPage.css';
import { LoadingOutlined, RightOutlined } from '@ant-design/icons';
import dx from '../../assets/dx.json';
import type { ContentListResponse, DXBlueprintContent } from '../../services/contentService';
import { useLanguage } from '../../contexts/LanguageContext';

const { Content } = Layout;

const MainPage: React.FC = () => {
  const { language } = useLanguage();
  
  const {
    unityProvider,
    loadingProgression,
    isLoaded,
    addEventListener,
    removeEventListener,
    sendMessage
  } = useUnityContext({
    loaderUrl: "/unity/build/build/Build/build.loader.js",
    dataUrl: "/unity/build/build/Build/build.data",
    frameworkUrl: "/unity/build/build/Build/build.framework.js",
    codeUrl: "/unity/build/build/Build/build.wasm",
    companyName: "SCG",
    productName: "dx-blueprint",
    productVersion: "1.0.0"
  });

  // Suppress Unity WebGL warnings that don't affect functionality
  useEffect(() => {
    const originalConsoleWarn = console.warn;
    const originalConsoleLog = console.log;
    
    // Filter out specific Unity warnings
    console.warn = (...args) => {
      const message = args.join(' ');
      if (message.includes('World animation alignment mode is not supported') ||
          message.includes('Animation alignment') ||
          message.includes('WebGL build target')) {
        return; // Suppress these specific warnings
      }
      originalConsoleWarn(...args);
    };
    
    console.log = (...args) => {
      const message = args.join(' ');
      if (message.includes('World animation alignment mode is not supported')) {
        return; // Suppress this specific log
      }
      originalConsoleLog(...args);
    };
    
    // Cleanup on unmount
    return () => {
      console.warn = originalConsoleWarn;
      console.log = originalConsoleLog;
    };
  }, []);

  const [showUnity, setShowUnity] = React.useState(false);
  
  // POI (Point of Interest) state
  const [showPOI, setShowPOI] = React.useState(false);
  const [poi, setPOI] = React.useState<{id: string, x: number, y: number}>({id: '', x: 0, y: 0});
  const [currentPoiData, setCurrentPoiData] = React.useState<{id: string, name: string, description: string} | null>(null);

  // API content data state
  const [apiContents, setApiContents] = React.useState<DXBlueprintContent[]>([]);
  const [contentsLoading, setContentsLoading] = React.useState(false);
  const [contentsFetched, setContentsFetched] = React.useState(false);

  // Fetch all contents from API
  const fetchAllContents = async () => {
    try {
      setContentsLoading(true);
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://api.your-backend.com'}/contents`);
      const data: ContentListResponse = await response.json();
      if (data && data.contents) {
        setApiContents(data.contents);
      }
    } catch (error) {
      console.error('❌ Failed to fetch contents:', error);
      setApiContents([]);
    } finally {
      setContentsLoading(false);
      setContentsFetched(true);
    }
  };

  const getDxData = useCallback((id: string) => {
    // First try to find in API contents (prioritize real data)
    if (apiContents.length > 0) {
      const apiContent = apiContents.find(item => 
        item.dx_number?.toLowerCase() === id.toLowerCase() || 
        item.SK?.toLowerCase() === id.toLowerCase() ||
        item.id?.toLowerCase() === id.toLowerCase()
      );
      if (apiContent) {
        const title = language === 'TH' 
          ? (apiContent.title_th || 'ไม่มีชื่อ')
          : (apiContent.title_en || apiContent.title_th || 'No title');
        
        const description = language === 'TH' 
          ? (apiContent.short_description_th || 'ไม่มีรายละเอียด')
          : (apiContent.short_description_en || apiContent.short_description_th || 'No description');
        
        return {
          id: apiContent.dx_number || apiContent.SK || apiContent.id,
          name: title,
          description: description
        };
      }
    }
    
    // Fallback to mock data if API content not found
    return dx.find(item => item.id === id);
  }, [language, apiContents]);

  const setupUnityContent = () => {
    // Send all API contents to Unity instead of mock data
    if (apiContents.length > 0) {
      apiContents.forEach(content => {
        // Send each content item to Unity with required fields
        const unityData = {
          id: content.dx_number || content.SK || content.id,
          dx_number: content.dx_number,
          SK: content.SK,
          slug: content.slug,
          created_at: content.created_at || '',
          updated_at: content.updated_at || '',
          title_th: content.title_th || '',
          short_description_th: content.short_description_th || '',
          short_description_en: content.short_description_en || '',
          status: content.status || 'draft'
        };
        sendMessage('Manager', 'AddPOIData', JSON.stringify(unityData));
      });
      sendMessage('Manager', 'Ready');
    } else {
      console.log('⚠️ No API contents available, Unity not populated');
    }
  };

  useEffect(() => {
    fetchAllContents();
  }, []);

  useEffect(() => {
    if (isLoaded && contentsFetched && apiContents.length > 0) {
      const timeout = setTimeout(() => {
        setupUnityContent();
      }, 1500);
      return () => clearTimeout(timeout);
    }
  }, [isLoaded, contentsFetched, apiContents, sendMessage]);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    if (isLoaded && contentsFetched) {
      timeout = setTimeout(() => setShowUnity(true), 1000);
    } else {
      setShowUnity(false);
    }
    return () => clearTimeout(timeout);
  }, [isLoaded, contentsFetched]);

  useEffect(() => {
    addEventListener("ShowPOIPanel", handleShowPOIPanel);
    return () => {
      removeEventListener("ShowPOIPanel", handleShowPOIPanel);
    };
  }, [addEventListener, removeEventListener]);

  // Update POI data when language or poi.id changes
  useEffect(() => {
    if (poi.id && showPOI) {
      const updatedData = getDxData(poi.id);
      if (updatedData) {
        setCurrentPoiData(updatedData);
      }
    } else {
      setCurrentPoiData(null);
    }
  }, [language, poi.id, showPOI, apiContents, getDxData]); // Re-run when language, poi.id, showPOI, apiContents, or getDxData change

  const handleShowPOIPanel = useCallback((id: string, x: number, y: number) => {
    const unityContainer = document.querySelector('.unity-container');
    const containerHeight = unityContainer ? unityContainer.clientHeight : window.innerHeight;
    const invertedY = containerHeight - y;
    
    const offsetX = x + 120;
    const offsetY = invertedY + 100;
    
    setPOI({ id, x: offsetX, y: offsetY });
    setShowPOI((id === "") ? false : true);
  }, []);

  const handleDetailClick = () => {
    // Find the corresponding API content to get the correct SK
    const apiContent = apiContents.find(item => 
      item.dx_number?.toLowerCase() === poi.id.toLowerCase() || 
      item.SK?.toLowerCase() === poi.id.toLowerCase() ||
      item.id?.toLowerCase() === poi.id.toLowerCase()
    );
    
    // Use SK from API content if available, otherwise fallback to poi.id
    const detailId = apiContent?.dx_number || poi.id;
    window.location.href = '/detail/' + detailId;
  };

  const poiPanel = () => {
    return (
      <div className="poi-panel-overlay" style={{ top: poi.y, left: poi.x }}>
        <Button
          type="primary"
          size="large"
          className="poi-button"
          onClick={handleDetailClick}
          icon={
            <div style={{
              backgroundColor: 'white',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <RightOutlined />
            </div>
          }
          iconPosition='end'
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          {poi.id.toUpperCase().replace(/-/g, '')}
        </Button>
        <h2 className="poi-title">{currentPoiData?.name || 'Loading...'}</h2>
        <p className="poi-description">{currentPoiData?.description || 'Loading...'}</p>
      </div>
    );
  };

  return (
    <Layout className="main-layout">
      <Navbar />
      <Content className="main-content">
        
        <div className="main-section">
          {(!showUnity) && (
            <div className="unity-loading">
              <ConfigProvider
                theme={{
                  token: {
                    colorPrimary: 'rgba(230, 206, 170, 1)', // Changed alpha to 1 for visibility
                  },
                }}
              >
                <Spin
                  indicator={<LoadingOutlined spin />}
                  size="large"
                />
              </ConfigProvider>
              <div className="loading-text">
                {contentsLoading && (
                  <p>Loading {0}%</p>
                )}
                {!contentsLoading && !contentsFetched && (
                  <p>Loading {0}%</p>
                )}
                {contentsFetched && !isLoaded && (
                  <p>Loading {Math.round(loadingProgression * 100)}%</p>
                )}
                {contentsFetched && isLoaded && (
                  <p>Initializing...</p>
                )}
              </div>
            </div>
          )}
          <div className="unity-container" style={{ display: showUnity ? 'flex' : 'none' }}>
            <Unity
              unityProvider={unityProvider}
              style={{
                width: '100%',
                height: '100%',
                border: 'none'
              }}
            />
            {showPOI && poiPanel()}
          </div>
        </div>
      </Content>
    </Layout>
  );
};

export default MainPage;
